import React, { useState } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Circle, X } from 'lucide-react';

/**
 * RecordingConsentBanner
 *
 * Shown to non-host participants when a recording starts.
 * - Dismissible banner with consent notice
 * - After dismissal, a persistent red recording indicator remains in the top bar
 *
 * Props:
 *   isRecording - boolean, whether the meeting is currently being recorded
 *   isHost      - boolean, hosts don't see this banner (they control recording)
 */
export function RecordingConsentBanner({ isRecording, isHost }) {
  const [dismissed, setDismissed] = useState(false);

  // Hosts never see the consent banner
  if (isHost || !isRecording) return null;

  // After dismissal, show only the persistent indicator
  if (dismissed) {
    return (
      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border-b border-red-200">
        <Circle className="h-2 w-2 fill-red-500 text-red-500 animate-pulse" />
        <span className="text-xs font-medium text-red-700">Recording</span>
      </div>
    );
  }

  // Full consent banner
  return (
    <div className="border-b border-red-200 bg-red-50">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <Circle className="h-3 w-3 fill-red-500 text-red-500 animate-pulse shrink-0" />
          <p className="text-sm text-red-800">
            <span className="font-semibold">This meeting is being recorded.</span>{' '}
            By staying, you consent to being recorded.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDismissed(true)}
          className="shrink-0 text-red-700 hover:text-red-900 hover:bg-red-100"
        >
          <X className="h-4 w-4 mr-1" />
          Dismiss
        </Button>
      </div>
    </div>
  );
}

export default RecordingConsentBanner;
